import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatDate, getErrorMessage } from "@/lib/api";
import {
  TYPE_LABELS, getDefaultSlots, dateStr,
} from "@/lib/workTrackingHelpers";

/**
 * AssignWorkDialog — assign one (or many) work entries to an employee or subcontractor.
 *
 * selection shape : { workerKind: "employee"|"subcontractor", workerId, dates: [yyyy-mm-dd], slot: "morning"|"afternoon"|"day" }
 */
const AssignWorkDialog = ({
  open,
  onOpenChange,
  selection,
  setSelection,
  employees,
  subcontractors,
  orders,
  savs,
  dates,
  viewMode,
  onAssigned,
}) => {
  const [assignType, setAssignType] = useState("commande");
  const [assignRef, setAssignRef] = useState("");
  const [assignSearch, setAssignSearch] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [useRangeMode, setUseRangeMode] = useState(false);
  const [rangeStartDate, setRangeStartDate] = useState("");
  const [rangeStartTime, setRangeStartTime] = useState("08:00");
  const [rangeEndDate, setRangeEndDate] = useState("");
  const [rangeEndTime, setRangeEndTime] = useState("16:12");

  // Reset state when the dialog opens with a new selection.
  useEffect(() => {
    if (!open || !selection) return;
    const dt = new Date(selection.dates[0] + "T12:00:00");
    const slots = getDefaultSlots(dt);
    setAssignType("commande");
    setAssignRef("");
    setAssignSearch("");
    setCustomLabel("");
    setUseCustomTime(false);
    setUseRangeMode(false);
    const ds = selection.dates[0];
    setRangeStartDate(ds);
    setRangeEndDate(ds);
    setRangeStartTime(slots.morning.start);
    setRangeEndTime(slots.afternoon.end);
    if (selection.slot === "morning") {
      setCustomStart(slots.morning.start); setCustomEnd(slots.morning.end);
    } else if (selection.slot === "afternoon") {
      setCustomStart(slots.afternoon.start); setCustomEnd(slots.afternoon.end);
    } else {
      setCustomStart(slots.morning.start); setCustomEnd(slots.afternoon.end);
    }
  }, [open, selection]);

  const handleAssign = async () => {
    if (!selection) return;
    if (assignType === "commande" && !assignRef) { toast.error("Sélectionnez une commande"); return; }
    if (assignType === "sav" && !assignRef) { toast.error("Sélectionnez un SAV"); return; }
    if (assignType === "custom" && !customLabel.trim()) { toast.error("Entrez un libellé"); return; }

    try {
      const batchEntries = [];
      const workerPayload = selection.workerKind === "subcontractor"
        ? { subcontractor_id: selection.workerId, employee_id: null }
        : { employee_id: selection.workerId, subcontractor_id: null };
      const refPayload = {
        ...workerPayload,
        entry_type: assignType,
        reference_id: assignType === "commande" || assignType === "sav" ? assignRef : null,
        custom_label: assignType === "custom" ? customLabel : null,
      };

      if (useRangeMode) {
        if (!rangeStartDate || !rangeEndDate) { toast.error("Dates de début et fin requises"); return; }
        if (rangeEndDate < rangeStartDate) { toast.error("La date de fin doit être après la date de début"); return; }
        if (rangeStartDate === rangeEndDate && rangeEndTime <= rangeStartTime) {
          toast.error("L'heure de fin doit être après l'heure de début");
          return;
        }
        const workingDays = [];
        const cursor = new Date(rangeStartDate + "T12:00:00");
        const endDt = new Date(rangeEndDate + "T12:00:00");
        while (cursor <= endDt) {
          const day = cursor.getDay();
          if (day !== 0 && day !== 6) workingDays.push(dateStr(new Date(cursor)));
          cursor.setDate(cursor.getDate() + 1);
        }
        if (workingDays.length === 0) { toast.error("Aucun jour ouvré dans la plage"); return; }

        workingDays.forEach((ds, idx) => {
          const dt = new Date(ds + "T12:00:00");
          const slots = getDefaultSlots(dt);
          const isFirst = idx === 0;
          const isLast = idx === workingDays.length - 1;
          const sameDay = workingDays.length === 1;

          let start, end;
          if (sameDay) { start = rangeStartTime; end = rangeEndTime; }
          else if (isFirst) { start = rangeStartTime; end = slots.afternoon.end; }
          else if (isLast) { start = slots.morning.start; end = rangeEndTime; }
          else { start = slots.morning.start; end = slots.afternoon.end; }

          const LUNCH_END = slots.morning.end;
          const LUNCH_START = slots.afternoon.start;
          const spansMorning = start < LUNCH_END;
          const spansAfternoon = end > LUNCH_START;

          if (spansMorning && spansAfternoon) {
            batchEntries.push({ date: ds, start_time: start, end_time: LUNCH_END, ...refPayload });
            batchEntries.push({ date: ds, start_time: LUNCH_START, end_time: end, ...refPayload });
          } else {
            batchEntries.push({ date: ds, start_time: start, end_time: end, ...refPayload });
          }
        });
      } else {
        for (const ds of selection.dates) {
          const dt = new Date(ds + "T12:00:00");
          const slots = getDefaultSlots(dt);

          if (selection.slot === "day" && !useCustomTime) {
            batchEntries.push({ date: ds, start_time: slots.morning.start, end_time: slots.morning.end, ...refPayload });
            batchEntries.push({ date: ds, start_time: slots.afternoon.start, end_time: slots.afternoon.end, ...refPayload });
          } else if (useCustomTime) {
            batchEntries.push({ date: ds, start_time: customStart, end_time: customEnd, ...refPayload });
          } else {
            const start = selection.slot === "morning" ? slots.morning.start : slots.afternoon.start;
            const end = selection.slot === "morning" ? slots.morning.end : slots.afternoon.end;
            batchEntries.push({ date: ds, start_time: start, end_time: end, ...refPayload });
          }
        }
      }

      await api.createWorkEntriesBatch({ entries: batchEntries });
      toast.success(`${batchEntries.length} entrée(s) créée(s)`);
      onOpenChange(false);
      onAssigned?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const headerLine = (() => {
    if (!selection) return "";
    const isSub = selection.workerKind === "subcontractor";
    const w = isSub
      ? subcontractors.find(s => s.id === selection.workerId)
      : employees.find(e => e.id === selection.workerId);
    const name = isSub
      ? (w?.company_name || `${w?.first_name || ""} ${w?.last_name || ""}`.trim() || "Sous-traitant")
      : `${w?.first_name || ""} ${w?.last_name || ""}`.trim();
    const slotLabel = selection.slot === "morning" ? "Matin" : selection.slot === "afternoon" ? "Après-midi" : "Journée";
    return `${isSub ? "ST · " : ""}${name} - ${selection.dates.map(d => formatDate(d)).join(", ")} (${slotLabel})`;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Assigner un créneau</DialogTitle>
          <DialogDescription>{headerLine}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Type *</Label>
            <Select value={assignType} onValueChange={v => { setAssignType(v); setAssignRef(""); }}>
              <SelectTrigger data-testid="assign-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {assignType === "commande" && (
            <div className="space-y-2">
              <Label>Commande *</Label>
              {(() => {
                const selected = orders.find((o) => o.id === assignRef);
                if (selected) {
                  return (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-indigo-300 bg-indigo-50 text-indigo-800 truncate max-w-[400px]">
                        {selected.client_name}
                        {selected.city ? ` — ${selected.city}` : ""}
                        {selected.order_type_name ? ` — ${selected.order_type_name}` : ""}
                      </Badge>
                      <Button size="sm" variant="ghost" onClick={() => setAssignRef("")} data-testid="assign-order-clear">Changer</Button>
                    </div>
                  );
                }
                return (
                  <div className="relative">
                    <Input
                      placeholder="Tapez nom client, ville ou type…"
                      value={assignSearch}
                      onChange={(e) => setAssignSearch(e.target.value)}
                      autoComplete="off"
                      data-testid="assign-order-search"
                    />
                    {assignSearch.trim() && (() => {
                      const term = assignSearch.trim().toLowerCase();
                      const filtered = orders.filter((o) => {
                        const ref = (o.reference || "").toLowerCase();
                        const c = (o.client_name || "").toLowerCase();
                        const city = (o.city || "").toLowerCase();
                        const tn = (o.order_type_name || "").toLowerCase();
                        return ref.includes(term) || c.includes(term) || city.includes(term) || tn.includes(term);
                      }).slice(0, 20);
                      if (filtered.length === 0) return null;
                      return (
                        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border rounded-md shadow-lg">
                          {filtered.map((o) => (
                            <button
                              key={o.id}
                              type="button"
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 border-b last:border-b-0 transition-colors"
                              onClick={() => { setAssignRef(o.id); setAssignSearch(""); }}
                            >
                              <span className="font-medium">{o.client_name}</span>
                              {o.city && <span className="text-muted-foreground"> — {o.city}</span>}
                              {o.order_type_name && <span className="text-indigo-700"> — {o.order_type_name}</span>}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>
          )}

          {assignType === "sav" && (
            <div className="space-y-2">
              <Label>SAV *</Label>
              <Select value={assignRef} onValueChange={setAssignRef}>
                <SelectTrigger data-testid="assign-sav"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>
                  {savs.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.client_name} - {s.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {assignType === "custom" && (
            <div className="space-y-2">
              <Label>Libellé *</Label>
              <Input value={customLabel} onChange={e => setCustomLabel(e.target.value)} placeholder="Description..." data-testid="assign-custom-label" />
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t">
            <input
              type="checkbox"
              id="range-mode"
              checked={useRangeMode}
              onChange={e => setUseRangeMode(e.target.checked)}
              data-testid="range-mode-toggle"
            />
            <label htmlFor="range-mode" className="text-sm cursor-pointer font-medium">
              Plage date + heure (début → fin)
            </label>
          </div>

          {useRangeMode && (
            <div className="space-y-3 p-3 rounded-xl border border-zinc-100 bg-blue-50/30">
              <p className="text-xs text-muted-foreground">
                Remplit tous les jours ouvrés (lun-ven) entre la date/heure de début et la date/heure de fin. Les jours complets intermédiaires sont pré-remplis avec les horaires standard.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Date de début</Label>
                  <Input type="date" value={rangeStartDate} onChange={e => setRangeStartDate(e.target.value)} data-testid="range-start-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Heure de début</Label>
                  <Input type="time" value={rangeStartTime} onChange={e => setRangeStartTime(e.target.value)} data-testid="range-start-time" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Date de fin</Label>
                  <Input type="date" value={rangeEndDate} onChange={e => setRangeEndDate(e.target.value)} data-testid="range-end-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Heure de fin</Label>
                  <Input type="time" value={rangeEndTime} onChange={e => setRangeEndTime(e.target.value)} data-testid="range-end-time" />
                </div>
              </div>
            </div>
          )}

          {!useRangeMode && (
            <div className="flex items-center gap-2 pt-2 border-t">
              <input type="checkbox" id="custom-time" checked={useCustomTime} onChange={e => setUseCustomTime(e.target.checked)} />
              <label htmlFor="custom-time" className="text-sm cursor-pointer">Horaires personnalisés</label>
            </div>
          )}

          {useCustomTime && !useRangeMode && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Début</Label>
                <Input type="time" value={customStart} onChange={e => setCustomStart(e.target.value)} data-testid="assign-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Fin</Label>
                <Input type="time" value={customEnd} onChange={e => setCustomEnd(e.target.value)} data-testid="assign-end" />
              </div>
            </div>
          )}

          {!useRangeMode && (
            <div className="flex gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => {
                if (!selection) return;
                setSelection({ ...selection, slot: "day" });
                setUseCustomTime(false);
              }}>
                Journée entière
              </Button>
              <Button variant="outline" size="sm" onClick={() => {
                if (!selection) return;
                const allDates = dates.map(d => dateStr(d));
                setSelection({ ...selection, dates: allDates, slot: selection.slot });
              }}>
                Toute la {viewMode === "week" ? "semaine" : "période"}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleAssign} data-testid="assign-submit">Assigner</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignWorkDialog;
